import * as React from 'react';
import { FC, useRef } from 'react';
import { style } from 'typestyle';
import { IMedia } from '../../../../types';
import VideoPlayer from './VideoPlayer';
import ImageDisplay from './ImageDisplay';
import UserName from '../../../UserName';
import { pillActiveBackgroundColor, pillBackgroundColor, pillFontColor } from '../../../consts';

interface MediaContainerProps {
  currentDisplayPost: IMedia;
  nextDisplayPost: IMedia | null;
  prevDisplayPost: IMedia | null;
  currentYOffset: number;
  showList: boolean;
  isMuted: boolean;
  isPlaying: boolean;
  togglePlay: () => void;
}

const MediaContainer: FC<MediaContainerProps> = ({
  currentDisplayPost,
  nextDisplayPost,
  prevDisplayPost,
  currentYOffset,
  showList,
  isMuted,
  isPlaying,
  togglePlay
}) => {
  const mediasContainerRef = useRef<HTMLDivElement>(null);
  const isVideo = currentDisplayPost.type === 'video';
  const isNextVideo = nextDisplayPost?.type === 'video';
  const isPrevVideo = prevDisplayPost?.type === 'video';

  return (
    <>
      <div 
        ref={mediasContainerRef}
        className={styles.mediasContainer}
        style={{ 
          transform: `translateY(${currentYOffset}%)`,
          filter: showList ? 'brightness(0.3)' : 'none',
        }}
      >
        {/* Previous Media (preloaded) */}
        {prevDisplayPost && (
          <div className={`${styles.mediaWrapper} ${styles.prevMedia}`}>
            {isPrevVideo ? (
              <VideoPlayer 
                post={prevDisplayPost} 
                preload
                isMuted={true}
                isPlaying={false}
                togglePlay={() => {}}
              />
            ) : (
              <ImageDisplay post={prevDisplayPost} />
            )}
          </div>
        )}

        {/* Current Media */}
        <div className={styles.mediaWrapper}>
          {isVideo ? (
            <VideoPlayer 
              post={currentDisplayPost}
              isMuted={isMuted}
              isPlaying={isPlaying}
              togglePlay={togglePlay}
              autoplay={true} 
            />
          ) : (
            <ImageDisplay post={currentDisplayPost} />
          )}
        </div>

        {/* Next Media (preloaded) */}
        {nextDisplayPost && (
          <div className={`${styles.mediaWrapper} ${styles.nextMedia}`}>
            {isNextVideo ? (
              <VideoPlayer 
                post={nextDisplayPost} 
                preload
                isMuted={true}
                isPlaying={false}
                togglePlay={() => {}}
              />
            ) : (
              <ImageDisplay post={nextDisplayPost} />
            )}
          </div>
        )}
      </div>

      <div className={styles.topLeft}>
        <UserName 
          color={pillFontColor}
          padding='10px 12px'
          backgroundColor={pillBackgroundColor}
          username={currentDisplayPost.username}
          source={currentDisplayPost.source}
          postUrl={currentDisplayPost.post}
        />
      </div>
    </>
  );
};

export default MediaContainer;

const styles = {
    mediasContainer: style({
        width: '100%',
        height: '100%',
        position: 'relative',
        transition: 'transform 0.05s linear',
    }),

    mediaWrapper: style({
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
    }),

    // Styles for previous and next media
    prevMedia: style({
        transform: 'translateX(-100%)', // Position off-screen to the left
        zIndex: 0,
    }),

    nextMedia: style({
        transform: 'translateX(100%)', // Position off-screen to the right
        zIndex: 0,
    }),

    topLeft: style({
        position: 'absolute',
        top: 20,
        left: 20,
        zIndex: 10,
        height: '40px',
    }),
}